import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { ChevronRight, Layers } from "lucide-react";
import type { ManualBodySection, ManualBodyStep } from "@arc/contracts";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

/** An element is missing from AR until it is placed. Only that counts: a step without text can be perfectly fine (owner 2026-11-02). */
export function stepNeedsAttention(step: Pick<ManualBodyStep, "elements">): boolean {
  return step.elements.some((el) => !el.isMain && !el.hasBeenPositioned);
}

/**
 * The folded outline: one dot per step, a gap between sections. Grey is the normal case, yellow means something is missing, the
 * big one is where you are (owner 2026-11-02). No green: a manual in good shape stays calm, so a yellow dot stands out. The
 * button on top unfolds the outline with its names.
 */
export function StepDots({
  sections,
  currentStepId,
  onSelectStep,
  onExpand,
  allSteps,
}: {
  sections: ManualBodySection[];
  currentStepId: number | null;
  onSelectStep: (stepId: number) => void;
  onExpand: () => void;
  allSteps?: { selected: boolean; onSelect: () => void } | null;
}) {
  const { t } = useTranslation();
  return (
    <nav aria-label={t("manualEditor.outline")} className="flex h-full w-full flex-col items-center gap-2 py-3">
      <TooltipTrigger delay={0} focusable={false}>
        <Button variant="outline" size="icon-sm" aria-label={t("manualEditor.expandOutline")} onPress={onExpand} className="dark:border-border shrink-0">
          <ChevronRight aria-hidden="true" className="size-4" />
        </Button>
        <Tooltip>{t("manualEditor.expandOutline")}</Tooltip>
      </TooltipTrigger>
      {allSteps ? (
        <TooltipTrigger delay={0} focusable={false}>
          <Button variant={allSteps.selected ? "default" : "ghost"} size="icon-sm" aria-label={t("manualEditor.allSteps")} aria-current={allSteps.selected ? "true" : undefined} onPress={allSteps.onSelect} className="shrink-0">
            <Layers aria-hidden="true" className="size-4" />
          </Button>
          <Tooltip>{t("manualEditor.allSteps")}</Tooltip>
        </TooltipTrigger>
      ) : null}
      <ul className="scroll-thin m-0 flex min-h-0 w-full list-none flex-col items-center overflow-y-auto p-0 py-1">
        {sections.map((section, i) => (
          <Fragment key={section.id}>
            {i > 0 ? <li aria-hidden="true" className="bg-border my-1.5 h-px w-4 shrink-0" /> : null}
            {section.steps.map((step) => {
              const current = step.id === currentStepId;
              const attention = stepNeedsAttention(step);
              const label = attention ? `${step.name}, ${t("manualEditor.notPlaced")}` : step.name;
              return (
                <li key={step.id} className="flex shrink-0">
                  {/* A plain button, so it needs the wrapper: without it the tooltip never opens. */}
                  <TooltipTrigger delay={0}>
                    <button
                      type="button"
                      aria-label={label}
                      aria-current={current ? "step" : undefined}
                      onClick={() => onSelectStep(step.id)}
                      className="focus-visible:ring-primary group/dot flex size-6 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent p-0 outline-none focus-visible:ring-2"
                    >
                      <span
                        className={cn(
                          "rounded-full transition-all",
                          current ? "bg-foreground ring-foreground/25 size-4 ring-4" : "group-hover/dot:size-3 size-2",
                          !current && (attention ? "bg-attention" : "bg-muted-foreground/60"),
                        )}
                      />
                    </button>
                    <Tooltip placement="right">{label}</Tooltip>
                  </TooltipTrigger>
                </li>
              );
            })}
          </Fragment>
        ))}
      </ul>
    </nav>
  );
}
